import { useEffect, useRef, useState, type ReactNode } from 'react'

/**
 * Caixa de visualização redimensionável para os campos de rich text
 * (Descrição / Resultado esperado). Quando `active` (modo leitura):
 *  - por padrão, a caixa tem a ALTURA DO CONTEÚDO, limitada a `maxHeight`
 *    (~15 linhas): conteúdo curto abre baixo; conteúdo longo abre em 15 linhas e
 *    rola/expande;
 *  - grip no canto inferior direito para aumentar/diminuir a área;
 *  - ícone "caber tudo" no canto superior direito (altura = todo o conteúdo);
 *  - a altura escolhida pelo usuário é memorizada POR ITEM (localStorage).
 * Quando inativa (edição), não constrange a altura — deixa o editor livre.
 */
export function ResizableField({
  storageKey,
  maxHeight,
  active,
  children,
}: {
  storageKey: string
  /** Teto da altura padrão (auto-ajuste ao conteúdo) — ex.: ~15 linhas. */
  maxHeight: number
  active: boolean
  children: ReactNode
}) {
  // null = modo automático (altura = conteúdo, teto maxHeight); número = fixado
  const [altura, setAltura] = useState<number | null>(() => {
    const salvo = typeof window !== 'undefined' ? window.localStorage.getItem(storageKey) : null
    const n = salvo ? Number(salvo) : NaN
    return Number.isFinite(n) && n > 0 ? n : null
  })
  // troca de item: recarrega a altura memorizada daquele item (ou volta ao auto)
  useEffect(() => {
    const salvo = window.localStorage.getItem(storageKey)
    const n = salvo ? Number(salvo) : NaN
    setAltura(Number.isFinite(n) && n > 0 ? n : null)
  }, [storageKey])

  const boxRef = useRef<HTMLDivElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const alturaRef = useRef<number | null>(altura)
  useEffect(() => {
    alturaRef.current = altura
  }, [altura])

  // "selecionado": só quando a caixa está em foco (o usuário clicou nela) é que
  // a roda do mouse rola DENTRO dela; fora disso, a roda rola o item inteiro.
  const [ativo, setAtivo] = useState(false)
  const ativoRef = useRef(false)
  useEffect(() => {
    ativoRef.current = ativo
  }, [ativo])
  useEffect(() => {
    if (!active) return
    const el = scrollRef.current
    if (!el) return
    const onWheel = (e: WheelEvent) => {
      if (ativoRef.current) return // caixa selecionada → rola dentro dela
      // não selecionada → rola o primeiro ancestral rolável (o item), não a caixa
      let cont = el.parentElement
      while (cont) {
        const st = getComputedStyle(cont)
        if ((st.overflowY === 'auto' || st.overflowY === 'scroll') && cont.scrollHeight > cont.clientHeight + 1) {
          e.preventDefault()
          cont.scrollTop += e.deltaY
          return
        }
        cont = cont.parentElement
      }
      e.preventDefault()
      window.scrollBy(0, e.deltaY)
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [active])

  const salvar = (h: number) => {
    const clamp = Math.max(60, Math.round(h))
    setAltura(clamp)
    window.localStorage.setItem(storageKey, String(clamp))
  }

  // estado ANTES do "caber tudo": null = não expandido; {h} guarda a altura
  // anterior (número) ou o modo automático (h: null) para o "recolher" voltar.
  const [antesDeExpandir, setAntesDeExpandir] = useState<{ h: number | null } | null>(null)
  useEffect(() => setAntesDeExpandir(null), [storageKey])

  const onGripDown = (e: React.MouseEvent) => {
    e.preventDefault()
    // arraste manual sai do modo "expandido" (o botão volta a ser expandir)
    setAntesDeExpandir(null)
    // parte da altura renderizada atual (funciona vindo do modo automático)
    const h0 = boxRef.current?.getBoundingClientRect().height ?? maxHeight
    const y0 = e.clientY
    const onMove = (ev: MouseEvent) => setAltura(Math.max(60, h0 + (ev.clientY - y0)))
    const onUp = () => {
      if (alturaRef.current != null) window.localStorage.setItem(storageKey, String(Math.round(alturaRef.current)))
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
      document.body.style.userSelect = ''
    }
    document.body.style.userSelect = 'none'
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }

  // expandir (caber tudo) x recolher (volta ao estado anterior à expansão)
  const expandido = antesDeExpandir !== null
  const caberTudo = () => {
    const el = scrollRef.current
    if (!el) return
    setAntesDeExpandir({ h: altura })
    salvar(el.scrollHeight + 2)
  }
  const recolher = () => {
    if (!antesDeExpandir) return
    if (antesDeExpandir.h == null) {
      // estado anterior era o automático (altura do conteúdo até o teto)
      setAltura(null)
      window.localStorage.removeItem(storageKey)
    } else {
      salvar(antesDeExpandir.h)
    }
    setAntesDeExpandir(null)
  }

  // a div de scroll recebe o tamanho: altura fixa (número) OU automática
  // (sem height, teto = maxHeight → cresce com o conteúdo até 15 linhas)
  const scrollStyle = altura != null ? { height: altura } : { maxHeight }

  // A ARVORE E SEMPRE A MESMA nos dois modos (2026-09-02): alternar entre
  // estruturas diferentes fazia o React REMONTAR os filhos quando `active`
  // virava — o RichTextEditor perdia o estado no meio do "entrar em edicao"
  // pelo lapis e a tela ficava travada. So estilos/atributos mudam.
  return (
    <div className={`wi-rzbox${active ? '' : ' wi-rzbox--free'}${ativo && active ? ' wi-rzbox--ativo' : ''}`} ref={boxRef}>
      <div
        className="wi-rzbox__scroll desc-scroll"
        style={active ? scrollStyle : { overflow: 'visible' }}
        ref={scrollRef}
        tabIndex={active ? 0 : -1}
        title={active ? 'Clique para rolar dentro do campo; fora dele a roda rola o item' : undefined}
        onFocus={() => setAtivo(true)}
        onBlur={() => setAtivo(false)}
      >
        {children}
      </div>
      {active ? (
        <>
          <button
            type="button"
            className="wi-rzbox__fit"
            title={expandido ? 'Recolher para o tamanho anterior' : 'Expandir para caber todo o conteúdo'}
            aria-label={expandido ? 'Recolher' : 'Expandir para caber todo o conteúdo'}
            onClick={expandido ? recolher : caberTudo}
          >
            <i className={`fas fa-${expandido ? 'compress' : 'expand'}`} aria-hidden />
          </button>
          <span
            className="wi-rzbox__grip"
            role="separator"
            aria-label="Arraste para redimensionar a área"
            title="Arraste para redimensionar"
            onMouseDown={onGripDown}
          />
        </>
      ) : null}
    </div>
  )
}
